import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { ErrorBag } from 'src/models/ErrorModel';
import { FormControl, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { COLORS } from 'src/constants/Color';
import { FORM_ID } from 'src/models/FormModel';
import { useCallback, useEffect, useMemo } from 'react';
import { getFundraisingListAction } from 'src/redux/actions/fundraisingAction';
import { FundraisingModel } from 'src/models/FundraisingModel';

const useStyles = makeStyles(() => ({
  formControl: {
    minWidth: '200px',
    marginTop: '4px',
    width: '100%',
  },
  link: {
    color: COLORS.primary,
    '&:hover': {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    marginBottom: 0,
  },
  link_disabled: {
    color: COLORS.darkgrey,
    marginBottom: 0,
  },
}));

interface Props {
  disabled?: boolean;
  form: FORM_ID;
  label: string;
  name: string;
  onChange: (id: string | number) => void;
  value?: string | number;
}

interface FundraisingItem {
  id: number | string;
  label?: string;
}

const FundraisingSelector = (props: Props) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  // const lang = useSelector((state: Store) => state.language.language ?? 'en');

  const {
    disabled,
    form,
    label,
    name,
    onChange,
    value,
  } = props;

  useEffect(() => {
    dispatch(getFundraisingListAction());
  }, []);

  const fundraisings = useSelector(
    (state: Store) => Object.values(state.fundraisings),
  ) as FundraisingModel[];
  const errors: ErrorBag = useSelector((state: Store) => state.errors[form] || {} as ErrorBag);

  const options = useMemo<FundraisingItem[]>(() => {
    if (!fundraisings.length) return [];

    return fundraisings
      .filter((item): item is FundraisingModel & {
        fundraisingInfo: { id: number | string; name: string }
      } => item.fundraisingInfo != null)
      .map((item) => ({
        id: item.fundraisingInfo.id.toString(),
        label: `${item.fundraisingInfo.name}:${item.fundraisingInfo.id.toString()}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [fundraisings]);

  const selectedValue = useMemo(() => {
    if (!fundraisings.length || !value) {
      return { id: 0, label: '' } as FundraisingItem;
    }
    const fundraising = fundraisings.find(
      (item) => item.fundraisingInfo?.id?.toString() === value?.toString(),
    );
    return fundraising
      ? {
        id: fundraising.fundraisingInfo?.id,
        label: fundraising.fundraisingInfo?.name,
      } as FundraisingItem
      : { id: 0, label: '' } as FundraisingItem;
  }, [fundraisings, value]);

  const handleValueChange = useCallback((item: FundraisingItem | null) => {
    onChange(item?.id ? item.id.toString() : ''); // or 0, depending on parent expectation
  }, [onChange]);

  const getOptionLabel = useCallback((option: FundraisingItem) => option.label || '', []);

  const filterOptions = useCallback((optionList: FundraisingItem[], state: any) => {
    const { inputValue } = state;
    if (inputValue.length <= 3) {
      return optionList; // Show all when ≤ 3 chars
    }
    return optionList.filter(
      (option) => option.label?.toLowerCase().includes(inputValue.toLowerCase()),
    );
  }, []);

  return (
    <>
      <FormControl
        variant="outlined"
        className={classes.formControl}
        key={`form-control-${name}`}>
        <Autocomplete
          disabled={disabled}
          value={selectedValue}
          onChange={(_, item) => handleValueChange(item)}
          options={options}
          filterOptions={filterOptions}
          getOptionLabel={getOptionLabel}
          getOptionSelected={(option, item) => option.id.toString() === item.id.toString()}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label={label}
              error={!!errors[name]}
              placeholder="Type 4+ chars to search fundraising" />
          )}
          key={`autocomplete-${name}`} />
      </FormControl>
    </>
  );
};

export default FundraisingSelector;
